import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import {
  CmsService,
  ImageGroup,
  ImageType,
  LanguageService,
  Price, Product,
  ScriptLoader,
  WindowRef
} from '@spartacus/core';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { IOverview, SdProductOverviewComponent } from '@seb/web-design-system/overview';
import { SdProductSellerModalComponent } from '@seb/web-design-system/product-seller';
import { BrandsEnum, IMediaGallery, ISkeeper, SdModalDefaultComponent } from '@seb/web-design-system/core';
import { PaypalBannerComponent } from '@seb/web-common/core/components/static/paypal-banner/paypal-banner.component';
import { StoreLocatorComponent } from '@seb/web-common/core/components/static/store-locator/store-locator.component';
import {
  ProductNotificationModalComponent
} from '@seb/web-common/core/components/static/product-notification-modal/product-notification-modal.component';
import { OneyModalComponent } from '@seb/web-common/core/components/static/oney-modal/oney-modal.component';
import {
  Bundle, CustomBaseStore, KlarnaContext, KlarnaData, ReinsuranceFooterModuleInterface, ReinsuranceModuleInterface,
  SkeeperShoppableContentInterface
} from '@seb/web-common/core/models';
import { ReinsurancesTypeEnum, RepairabilityEnum } from '@seb/web-common/core/enums';
import { BundleMapper } from '@seb/web-common/core/mappers';
import { BaseStoreService, CartFacadeService, ComponentService } from '@seb/web-common/core/services';
import { IInfoBanner, SdInfoBannerComponent } from '@seb/web-design-system/info-banner';
import {
  SkeeperShoppableContentComponent
} from '@seb/web-common/core/components/static/skeeper-shoppable-content/skeeper-shoppable-content.component';

@Component({
  selector: 'wc-bundle-overview',
  templateUrl: './bundle-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdInfoBannerComponent,
    SdProductOverviewComponent,
    PaypalBannerComponent,
    SdModalDefaultComponent,
    StoreLocatorComponent,
    SdProductSellerModalComponent,
    ProductNotificationModalComponent,
    OneyModalComponent,
    TranslatePipe,
    SkeeperShoppableContentComponent
  ]
})
export class BundleOverviewComponent implements OnDestroy {
  @Input()
  isSSR = true;

  @Input() set bundle(currentBundle: Bundle) {
    if (currentBundle) {
      const mainProduct = currentBundle?.products[0];
      const isForfait = mainProduct.productGenre === 'Forfait';
      this.initSkeeperShoppableContent(mainProduct);
      const cmpReinsurances$ = this.cmsService.getContentSlot('Reinsurances').pipe(
        switchMap((component) => {
          return !!component.components && component.components.length > 0
            ? this.cmsService.getComponentData(component.components[0].uid).pipe(
              filter((cmp) => !!cmp),
              switchMap((cmp) => this.componentService.getLazyComponentData(cmp, 'components'))
            )
            : of(null);
        })
      );
      const cmpReinsurancesWithLinks$ = isForfait
        ? of(null)
        : cmpReinsurances$.pipe(
          switchMap((cmpReinsurances: ReinsuranceModuleInterface) => {
            const cmpLinks: Observable<ReinsuranceModuleInterface>[] =
              (!!cmpReinsurances?.componentsObjects && cmpReinsurances?.componentsObjects.length > 0
                ? cmpReinsurances?.componentsObjects.map((cmp) => {
                  return this.componentService.getLazyComponentData(cmp, 'components').pipe(filter((cmp) => !!cmp));
                })
                : of([])) as Observable<ReinsuranceModuleInterface>[];
            return combineLatest([of(cmpReinsurances?.title), combineLatest(cmpLinks)]);
          })
        );

      this.subscriptions.add(
        cmpReinsurancesWithLinks$.subscribe(
          (cmpReinsurancesServicesAndWarranty) => {
            const _activeBrands = this.baseStore.houseOfBrandConfig?.brand;
            const isHob = _activeBrands?.length > 0;
            const brandFound = this.baseStoreService.getBrand(currentBundle['brand']);

            this.currentBundle = currentBundle;

            if (currentBundle.klarnaContext) {
              this.initKlarnaScript(currentBundle.klarnaContext);
            }

            let _cmpReinsurancesServicesAndWarranty: ReinsuranceFooterModuleInterface = {};
            let _cmpReinsurancesServicesAndWarrantyReinsurances = [];

            if (cmpReinsurancesServicesAndWarranty) {
              const isReparability = !!mainProduct && mainProduct.reparabilityIncluded === RepairabilityEnum.YES;
              const repairabilityStatus = mainProduct.repairabilityStatus;

              _cmpReinsurancesServicesAndWarrantyReinsurances = cmpReinsurancesServicesAndWarranty[1]?.filter((cmp) => {
                if (cmp.ReinsuranceType === ReinsurancesTypeEnum.REPAIRABILITY && isReparability) {
                  if (repairabilityStatus) {
                    const isRepairability10Y = repairabilityStatus.includes('_10Y_');
                    const isRepairability15Y = repairabilityStatus.includes('_15Y_');
                    if (isRepairability15Y) {
                      cmp.headline = cmp.headline.replace('10', '15');
                    } else if (isRepairability10Y) {
                      cmp.headline = cmp.headline.replace('15', '10');
                    }
                  }
                }

                return isReparability
                  ? cmp.ReinsuranceType === ReinsurancesTypeEnum.CLIENT_SERVICE ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.EXTENDED_WARRANTY ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.REPAIRABILITY ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.WARRANTY
                  : cmp.ReinsuranceType === ReinsurancesTypeEnum.CLIENT_SERVICE ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.EXTENDED_WARRANTY ||
                      cmp.ReinsuranceType === ReinsurancesTypeEnum.WARRANTY;
              });

              _cmpReinsurancesServicesAndWarranty = {
                headline: cmpReinsurancesServicesAndWarranty[0],
                componentsObjects: _cmpReinsurancesServicesAndWarrantyReinsurances
              };
            }

            this.overview = this.bundleMapper.bundleToIOverview(
              currentBundle,
              brandFound,
              this.baseStore.displayTaxesIncluded,
              _cmpReinsurancesServicesAndWarranty,
              isHob
            );

            if (
              brandFound &&
              this.translateService.instant('product.brandcorner.banner.text') !== 'product.brandcorner.banner.text' &&
              this.translateService.instant('product.brandcorner.banner.text')?.trim()?.length > 0
            ) {
              this.showBrandCornerBanner(brandFound, currentBundle.name);
            }

            this.price = currentBundle.discountedPrice;
            this.displayPaypalBanner =
              this.overview.onSale && this.bundleMapper.stockMapper(currentBundle) && !!this.overview?.price;
            if (this.displayPaypalBanner) {
              this.subscriptions.add(
                this.languageService
                  .getActive()
                  .pipe(take(1))
                  .subscribe((currentLang) => {
                    this.activeLanguage = currentLang;
                  })
              );
            }
            this.images = (currentBundle.galleryImages[ImageType.GALLERY] as ImageGroup[])
              ? ((currentBundle.galleryImages[ImageType.GALLERY] as ImageGroup[])
                .filter((value) => !!value && Object.keys(value).length !== 0)
                .map((imageGroup) => ({
                  extra: { ...imageGroup['extra'] },
                  icon: { ...imageGroup['icon'] },
                  main: { ...imageGroup['main'] },
                  original: { ...imageGroup['original'] },
                  thumbnail: { ...imageGroup['thumbnail'] }
                })) as IMediaGallery[])
              : ([] as IMediaGallery[]);
            this.hasLoaded.emit();
          }
        )
      );
    }
  }

  @Input() isLoading = true;

  @Input() brand = BrandsEnum.default;

  @Output() hasLoaded = new EventEmitter<void>();

  @Input()
  showSkeeper = false;

  @Input()
  objSkeeper: ISkeeper = {};

  overview: IOverview;
  images: IMediaGallery[] = [];
  isStoreModalOpened = false;
  notifyModalOpened = false;
  klarnaData: KlarnaData;
  isOpenedSellerModal = false;
  isOpenedOneyModal = false;
  currentBundle: Bundle;

  POSITION = 'product';
  price?: Price;
  displayPaypalBanner = false;
  activeLanguage: string;
  isBannerVisible = false;
  hasVideos = true;
  bannerData: IInfoBanner = {};
  skeeperShoppableContentData: SkeeperShoppableContentInterface;

  private readonly context = 'Main Product';
  private baseStore: CustomBaseStore;
  private subscriptions = new Subscription();

  constructor(
    private bundleMapper: BundleMapper,
    private cartService: CartFacadeService,
    private scriptLoader: ScriptLoader,
    private languageService: LanguageService,
    private cdRef: ChangeDetectorRef,
    private baseStoreService: BaseStoreService,
    private windowRef: WindowRef,
    private translateService: TranslateService,
    private componentService: ComponentService,
    private cmsService: CmsService,
    @Inject(DOCUMENT) private document: any
  ) {
    this.baseStore = this.baseStoreService.baseStore;
  }

  openSellerModal(): void {
    this.isOpenedSellerModal = true;
  }

  addToCart(quantity: number): void {
    sessionStorage.setItem('addToCartContext', this.context);
    this.cartService.addBundleToCartShippingType(this.currentBundle, quantity);
  }

  notifyMe(): void {
    this.notifyModalOpened = true;
  }

  dividedClick(): void {
    this.isOpenedOneyModal = true;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  initKlarnaScript(klarna: KlarnaContext): void {
    if (this.document?.querySelector('#klarna-script')) {
      this.klarnaData = {
        price: klarna.price,
        locale: klarna.locale
      };
      this.cdRef.markForCheck();
      return;
    }
    this.scriptLoader.embedScript({
      src: klarna.jsURL,
      attributes: { 'data-client-id': klarna.clientId, id: 'klarna-script' },
      callback: () => {
        this.klarnaData = {
          price: klarna.price,
          locale: klarna.locale
        };
        this.cdRef.markForCheck();
      }
    });
  }

  showBrandCornerBanner(brandFound, bundleName) {
    this.isBannerVisible = true;
    this.bannerData = {
      siteBrand: this.baseStore.brandSite,
      brand: brandFound.id,
      itemName: bundleName,
      website: this.windowRef.location.origin
    };
  }

  bannerVisibilityChange() {
    this.isBannerVisible = false;
  }

  initSkeeperShoppableContent(product: Product) {
    const titleSection = this.translateService.instant('shoppable.section.title');
    this.skeeperShoppableContentData = {
      title: titleSection,
      product: product?.code
    };
    this.cdRef.detectChanges();
  }

  checkHasVideo(event) {
    this.hasVideos = event;
  }
}
